import { iif, Observable, timer, throwError } from 'rxjs';
import { retry } from 'rxjs/operators';

export function getBackoff(
  attempt: number,
  interval: number,
  exponent: number,
  maximum: number
): number {
  return Math.min(maximum, interval * Math.pow(attempt, exponent));
}

/**
 * Retry an operation with exponental backoff.
 *
 * @param maxRetries - number of times to retry with backoff.
 * @param shouldRetry - a function that accepts an error and returns true if this should retry.
 * @param initInterval - the initial backoff interval.
 * @param maxInterval - the maximum backoff interval
 * @param exponent - the exponent used for each backoff calculation.
 * @returns an Operator function to use with {@link Observable#pipe}
 */
export default function retryWithBackoff<P>(
  maxRetries: number,
  shouldRetry: (error: P) => boolean = (): boolean => true,
  initInterval = 500,
  maxInterval = 30000,
  exponent = 2
): <T>(source: Observable<T>) => Observable<T> {
  return <T>(source: Observable<T>): Observable<T> =>
    source.pipe(
      retry({
        delay: (err: P, index) =>
          iif(
            () => index <= maxRetries && shouldRetry(err),
            timer(getBackoff(index, initInterval, exponent, maxInterval)),
            throwError(() => err)
          )
      })
    );
}
